import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCheckIn } from '@/hooks/useCheckIn';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { MapPin } from 'lucide-react';

// This component handles QR code redirects for attendance check-in
export function AttendanceCheckInHandler() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const { checkInToEvent } = useCheckIn();
  const [needsLocation, setNeedsLocation] = useState(false);
  const [event, setEvent] = useState<any>(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  useEffect(() => {
    const handleCheckIn = async () => {
      const eventId = searchParams.get('eventId');
      const title = searchParams.get('title');
      
      if (!eventId) {
        toast.error('Invalid check-in link');
        navigate('/attendance');
        return;
      }

      // Wait for auth to finish loading
      if (loading) {
        return;
      }

      if (!user) {
        // Save this URL (including eventId) so we can resume after login
        const currentUrl = `${window.location.pathname}${window.location.search}`;
        sessionStorage.setItem('redirectAfterLogin', currentUrl);
        toast.error('Please log in to check in');
        navigate('/auth');
        return;
      }

      try {
        console.log('🎯 Starting check-in process for eventId:', eventId, 'userId:', user.id);
        
        // Get event details for the check-in
        const { data: event, error: eventError } = await supabase
          .from('events')
          .select('*')
          .eq('id', eventId)
          .single();

        if (eventError) {
          throw new Error('Event not found');
        }

        console.log('🔍 Event data received:', { 
          eventId, 
          requires_gps: event.requires_gps,
          event_format: event.event_format,
          fullEvent: event 
        });

        // If event requires GPS, prompt user to grant location access
        if (event.requires_gps) {
          console.log('📍 Event requires GPS, prompting for location', { eventId, requires_gps: event.requires_gps });
          setEvent(event);
          setNeedsLocation(true);
          return;
        }

        console.log('⏭️ Event does not require GPS, proceeding with standard check-in');

        // Use useCheckIn hook with QR method (no location for non-GPS events)
        const success = await checkInToEvent(eventId, event, undefined, 'qr');
        
        if (success) {
          navigate('/attendance?tab=dashboard');
        } else {
          // Error already shown by useCheckIn
          navigate('/attendance');
        }
        
      } catch (error) {
        console.error('💥 Check-in error:', error);
        toast.error('Failed to check in. Please try again.');
        navigate('/attendance');
      }
    };

    if (searchParams.get('eventId')) {
      handleCheckIn();
    } else {
      navigate('/attendance');
    }
  }, [searchParams, loading]); // Removed user from dependencies to prevent re-runs

  const handleGetLocation = async () => {
    if (!event) return;
    
    setGettingLocation(true);
    console.log('📍 Requesting current position for GPS-required event', { eventId: searchParams.get('eventId') });
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });
      
      const userLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      };

      console.log('📍 Location obtained from browser', userLocation);
      console.log('🎫 Proceeding to check-in with location for event', searchParams.get('eventId'));
      // Now check in with location
      const success = await checkInToEvent(searchParams.get('eventId')!, event, userLocation, 'qr');
      
      if (success) {
        navigate('/attendance?tab=dashboard');
      } else {
        navigate('/attendance');
      }
    } catch (locationError: any) {
      console.error('📵 Location error while attempting GPS check-in:', locationError);
      const code = (locationError && (locationError.code ?? locationError.name)) ?? 'UNKNOWN';
      const message = (locationError && locationError.message) || String(locationError);
      console.log('Location error details:', { code, message });
      toast.error('Unable to get your location. Please ensure location services are enabled in your browser settings.');
      setGettingLocation(false);
    }
  };

  // Show location permission prompt if needed
  if (needsLocation && event) {
    return (
      <div className="flex items-center justify-center min-h-screen safe-area-padding-bottom px-4">
        <div className="text-center w-full max-w-sm space-y-6">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <MapPin className="h-8 w-8 text-primary" />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">Location Required</h2>
            <p className="text-muted-foreground text-sm">
              This event requires GPS verification to confirm your attendance. We'll only access your location once for check-in - no continuous tracking.
            </p>
            <div className="flex items-center justify-center gap-1.5 text-xs text-primary bg-primary/5 rounded-md px-3 py-2">
              <MapPin className="h-3.5 w-3.5" />
              <span>One-time verification only</span>
            </div>
          </div>
          <Button 
            onClick={handleGetLocation} 
            disabled={gettingLocation}
            className="w-full"
            size="lg"
          >
            {gettingLocation ? 'Getting Location...' : 'Allow Location Access'}
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/attendance')}
            className="w-full"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen safe-area-padding-bottom px-4">
      <div className="text-center w-full max-w-sm">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-6"></div>
        <h2 className="text-lg font-semibold mb-2">Processing Check-in</h2>
        <p className="text-muted-foreground text-sm">Please wait while we verify your attendance...</p>
      </div>
    </div>
  );
}